import * as React from "react";
import { UnmountClosed } from "react-collapse";
import { MoneyDisplayHelper } from "../../../../../../../react-core/Helpers/MoneyDisplayHelper";
import { BaseComponent } from "../../../../../../../react-core/Html/BaseComponent";
import { IJsonDate } from "../../../../../../../react-core/Utils/JsonDateParser";
import { CurrencyCode } from "../../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { AssetClassType } from "../../../../../../../Wrap/Constants/Enums/Domain/Instrument/AssetClassType";
import { ProductType } from "../../../../../../../Wrap/Constants/Enums/Domain/ProductType";
import { UserClaim } from "../../../../../../../Wrap/Constants/Enums/UserClaim";
import { Money } from "../../../../../../../Wrap/Core/DataTypes/Money";
import { InvestmentsColumns } from "../../../../../../../Wrap/Models/Domains/Users/UserPageColumnSetting/InvestmentsColumns";
import { Investment } from "../../../../../../../Wrap/Models/Models/PortfolioAnalysis/Investment/Investment";
import { Permission } from "../../../../../../Shared/Permission/Permission";
import { InvestmentType } from "../../Resource/Contents";
import { InvestmentDetailsPopupModal } from "../../../Common/InvestmentDetailsPopUp/InvestmentDetailsPopupModal";
import { InvestmentsColConfigHelper } from "../../../Common/InvestmentDetailsPopUp/InvestmentsColConfigHelper";
import { IInvestmentGroup, IInvestmentWithToggle, IProductTableData, ITotal } from "./Store/InvestmentsStatic";
import { Urls } from "../../../../../Urls";
import { Navigator } from "../../../../../../Shared/Wizards/Entities/Navigator";
import { PortfolioDashboardStore } from "../../Store/Variable";
import { ContextStore } from "../../../../../../../react-core/Flux/Variables";
import { ContextType } from "../../../../../../../Wrap/Constants/Enums/ContextType";
import { CheckboxEditor } from "../../../../../../../react-core/Html/CheckboxEditor";
import { ClientAccount } from "../../../../../../../Wrap/Models/Domains/Account/SlAccounts/ClientAccount";
import { SubAccount } from "../../../../../../../Wrap/Models/Domains/Account/SlAccounts/SubAccount";
import { ServiceProposition } from "../../../../../../../Wrap/Constants/Enums/ServiceProposition";
import { SubAccountTypeHelper } from "../../../Common/Helpers/SubAccountTypeHelper";
import AdviserSiteTagManager, {
    eventCategoryForGTM,
} from "../../../../../../Shared/Cookies/ChoicesCookie/AdviserSiteTagManager";
import { TransactionEnum } from "../Transactions/Resource/Contents";
import { DepositProduct } from "../../../../../../../Wrap/Models/Domains/Investment/DepositProduct";
import { DepositDetailsPopupModal } from "../../../Common/DepositDetailsPopUp/DepositDetailsPopupModal";

interface ICommonViewTbodyProps {
    item: IProductTableData;
    keyName: string;
    isAssetView?: boolean;
    assetClassType: AssetClassType;
    asAtDate: Date;
    cols: any[];
    stringHelper: any;
    isEditTreasure: boolean;
    onTreasuredAssetChange: (productId: number, value: boolean, subAccountId: string) => void;
    cashProducts: Record<number, DepositProduct>
}

interface ICommonViewTbodyState {
    isShowDetailModal: boolean;
    instrumentCode: string;
    productId: number;
    instrumentName: string;
    asAt: IJsonDate;
    unitCost: Money;
    unitPrice: Money;
    marketValue: Money;
    securityType: ProductType;
    toggles: Map<number, boolean>;
}

export class CommonViewTbody extends BaseComponent<ICommonViewTbodyProps, ICommonViewTbodyState> {
    constructor(props: ICommonViewTbodyProps) {
        super(props);
        this.state =
            {
                isShowDetailModal: false,
                instrumentCode: null,
                productId: 0,
                instrumentName: null,
                asAt: null,
                unitCost: null,
                unitPrice: null,
                marketValue: null,
                securityType: null,
                toggles: new Map<number, boolean>(),
            };
    }

    private addEvent = new AdviserSiteTagManager().addEvent;

    private getMoneyFormat(val: Money, isShowCurrency?: boolean, decimalCount?: number): string {
        let formatMoney = MoneyDisplayHelper.FromatMoneyDisplay(
            Math.abs(val.Amount),
            val.Currency,
            isShowCurrency,
            decimalCount
        );
        if (val.Amount < 0) {
            formatMoney = "-" + formatMoney;
        }
        return formatMoney;
    }

    private isCash(instrumentCode: string): boolean {
        return instrumentCode.toLowerCase().indexOf("cash") > -1;
    }

    private isOpi(instrumentCode: string): boolean {
        return instrumentCode.toLowerCase().indexOf("pa_") > -1;
    }

    private getQuantity(entry: Investment): string {
        return this.getMoneyFormat({ Amount: entry.Quantity, Currency: CurrencyCode.GBP }, false, 4);
    }

    private getUnitCost(entry: Investment): string {
        return this.isOpi(entry.InstrumentCode) ? "-" : this.getMoneyFormat(entry.UnitCost, false, 4);
    }

    private getUnitPrice(entry: Investment): string {
        return this.getMoneyFormat(entry.UnitPrice, false, 4);
    }

    private getDifferenceClass(val: number, isHeader: boolean): string {
        if (isHeader) {
            return val == 0
                ? " cell-right text-highlight"
                : val > 0
                ? " cell-right text-nowrap text-positive "
                : " cell-right text-nowrap text-negative ";
        } else {
            return val == 0
                ? " cell-right "
                : val > 0
                ? " cell-right text-nowrap text-positive "
                : " cell-right text-nowrap text-negative ";
        }
    }

    private renderRowForTotal(col: InvestmentsColumns, entry: ITotal, isHeader: boolean) {
        switch (col) {
            case InvestmentsColumns.Allocation:
                return <td className="cell-right">{(entry.Allocation * 100).toFixed(2)}%</td>;
            case InvestmentsColumns.TotalCost:
                return <td className="cell-right">{this.getMoneyFormat(entry.TotalCost)}</td>;
            case InvestmentsColumns.MarketValue:
                return <td className="cell-right">{this.getMoneyFormat(entry.MarketValue)}</td>;
            case InvestmentsColumns.Difference:
                return (
                    <td className="cell-right">
                        <span className={this.getDifferenceClass(entry.Difference.Amount, isHeader)}>
                            {this.getMoneyFormat(entry.Difference)}
                        </span>
                    </td>
                );
            case InvestmentsColumns.Quantity:
            case InvestmentsColumns.Location:
            case InvestmentsColumns.UnitCost:
            case InvestmentsColumns.UnitPrice:
            case InvestmentsColumns.FundCode:
            case InvestmentsColumns.TargetAllocation:
                return <td className="cell-right">-</td>;
            case InvestmentsColumns.TreasuredAsset:
                return <td className="cell-center">-</td>;
        }
    }

    private renderRowForManagedPortfolio(col: InvestmentsColumns, entry: IInvestmentWithToggle, isHeader: boolean) {
        switch (col) {
            case InvestmentsColumns.Allocation:
                return <td className="cell-right">{(entry.Allocation * 100).toFixed(2)}%</td>;
            case InvestmentsColumns.TotalCost:
                return <td className="cell-right">{this.getMoneyFormat(entry.TotalCost)}</td>;
            case InvestmentsColumns.MarketValue:
                return <td className="cell-right">{this.getMoneyFormat(entry.MarketValue)}</td>;
            case InvestmentsColumns.Difference:
                return (
                    <td className="cell-right">
                        <span className={this.getDifferenceClass(entry.Difference.Amount, isHeader)}>
                            {this.getMoneyFormat(entry.Difference)}
                        </span>
                    </td>
                );
            case InvestmentsColumns.Location:
                if (entry.Location != "CTAIn" && entry.Location != "CATOut") {
                    return <td className="cell-right">{entry.Location}</td>;
                }
            case InvestmentsColumns.Quantity:
            case InvestmentsColumns.UnitCost:
            case InvestmentsColumns.UnitPrice:
                return <td className="cell-right">-</td>;
            case InvestmentsColumns.FundCode:
                return <td className="cell-right">{entry.InstrumentCode}</td>;
            case InvestmentsColumns.TargetAllocation:
                return <td className="cell-right">{entry.InstrumentWeighting.toFixed(2)}</td>;
            case InvestmentsColumns.TreasuredAsset:
                const allAccounts = PortfolioDashboardStore.getData();
                let subAccount: SubAccount = null;
                let headAccount: ClientAccount = null;
                allAccounts.forEach((x) => {
                    const _subAccount = x.SubAccounts.find((y) => y.ClAccountId == this.props.item.ClAccountId);
                    if (_subAccount) {
                        headAccount = x;
                        subAccount = _subAccount;
                    }
                });
                if (
                    headAccount?.AccountServicePropositionId == ServiceProposition.Wrap &&
                    !SubAccountTypeHelper.isLegacyProduct(subAccount.SubAccountType)
                ) {
                    const isTreasuredAsset = (this.props.item?.EditTreasureAssets || []).includes(entry.ProductId);
                    return (
                        <td className={"cell-right"}>
                            <CheckboxEditor
                                value={isTreasuredAsset}
                                onChange={(v) =>
                                    this.props.onTreasuredAssetChange(entry.ProductId, v, subAccount.ClAccountId)
                                }
                                cssWrapper="square-checkbox mb-0"
                                disabled={
                                    (this.props.item?.RingfencedInstruments || []).includes(entry?.InstrumentCode) ||
                                    !this.props.isEditTreasure
                                }
                            />
                        </td>
                    );
                } else {
                    return <td className="cell-center">-</td>;
                }
        }
    }

    private renderRowForInvestment(
        col: InvestmentsColumns,
        entry: IInvestmentWithToggle,
        tdclass: string,
        hideTargetAllocation: boolean,
        index: number,
        isModel?: boolean
    ) {
        const allAccounts = PortfolioDashboardStore.getData();
        let subAccount: SubAccount = null;
        let headAccount: ClientAccount = null;
        allAccounts.forEach((x) => {
            const _subAccount = x.SubAccounts.find((y) => y.ClAccountId == this.props.item.ClAccountId);
            if (_subAccount) {
                headAccount = x;
                subAccount = _subAccount;
            }
        });
        const customerHierarchyId = ContextStore.getUrlParamsHierarchyId(ContextType.Customer);
        switch (col) {
            case InvestmentsColumns.Allocation:
                return <td className={tdclass}>{(entry.Allocation * 100).toFixed(2)}%</td>;
            case InvestmentsColumns.TotalCost:
                return <td className={tdclass}>{this.getMoneyFormat(entry.TotalCost)}</td>;
            case InvestmentsColumns.MarketValue:
                return <td className={tdclass}>{this.getMoneyFormat(entry.MarketValue)}</td>;
            case InvestmentsColumns.Difference:
                return (
                    <td className={tdclass}>
                        <span className={this.getDifferenceClass(entry.Difference.Amount, false)}>
                            {this.getMoneyFormat(entry.Difference)}
                        </span>
                    </td>
                );
            case InvestmentsColumns.Quantity:
                return (
                    <td className={tdclass}>
                        {entry.Type == ProductType.Cash && (
                            <Permission
                                userClaim={UserClaim.AdviserPortalInvestmentTransactionCanView}
                                replaceChildren={this.getQuantity(entry)}
                            >
                                <button
                                    className="hyperlink"
                                    onClick={() => {
                                        this.addEvent({
                                            event: "investmentstab_interaction",
                                            eventParameters: {
                                                eventCategory: eventCategoryForGTM.investmentsTab,
                                                eventAction: "interactions",
                                                eventLabel: "quantity click",
                                            },
                                        });
                                        Navigator.navigateTo(
                                            Urls.get().main.PortfolioDashboard({ hierarchyId: customerHierarchyId })
                                                .transactions,
                                            {
                                                scrollAfterNavigating: true,
                                                queryParamsObject: {
                                                    transtypeselect: TransactionEnum.cash,
                                                    investmentinstrumentcode: entry.InstrumentCode,
                                                    hierarchyid: subAccount ? subAccount.HierarchyId : "",
                                                },
                                            }
                                        );
                                    }}
                                >
                                    {this.getQuantity(entry)}
                                </button>
                            </Permission>
                        )}
                        {entry.Type != ProductType.Cash && (
                            <Permission
                                userClaim={UserClaim.AdviserPortalInvestmentTransactionCanView}
                                replaceChildren={this.getQuantity(entry)}
                            >
                                <button
                                    className="hyperlink"
                                    onClick={() => {
                                        this.addEvent({
                                            event: "investmentstab_interaction",
                                            eventParameters: {
                                                eventCategory: eventCategoryForGTM.investmentsTab,
                                                eventAction: "interactions",
                                                eventLabel: "quantity click",
                                            },
                                        });
                                        Navigator.navigateTo(
                                            Urls.get().main.PortfolioDashboard({ hierarchyId: customerHierarchyId })
                                                .transactions,
                                            {
                                                scrollAfterNavigating: true,
                                                queryParamsObject: {
                                                    transtypeselect: TransactionEnum.investments,
                                                    investmentinstrumentcode: entry.InstrumentCode,
                                                    hierarchyid: subAccount ? subAccount.HierarchyId : "",
                                                },
                                            }
                                        );
                                    }}
                                >
                                    {this.getQuantity(entry)}
                                </button>
                            </Permission>
                        )}
                    </td>
                );
            case InvestmentsColumns.Location:
                if (entry.Location != "CTAIn" && entry.Location != "CATOut") {
                    return <td className="cell-right">{entry.Location}</td>;
                }
            case InvestmentsColumns.UnitCost:
                return <td className={tdclass}>{this.getUnitCost(entry)}</td>;
            case InvestmentsColumns.UnitPrice:
                return <td className={tdclass}>{this.getUnitPrice(entry)}</td>;
            case InvestmentsColumns.FundCode:
                return <td className={tdclass}>{entry.InstrumentCode}</td>;
            case InvestmentsColumns.TargetAllocation:
                return <td className={tdclass}>{hideTargetAllocation ? "-" : entry.InstrumentWeighting.toFixed(2)}</td>;
            case InvestmentsColumns.TreasuredAsset:
                if (
                    entry.InstrumentCode !== "GBPCash" &&
                    headAccount.AccountServicePropositionId == ServiceProposition.Wrap &&
                    !SubAccountTypeHelper.isLegacyProduct(subAccount.SubAccountType) &&
                    !isModel
                ) {
                    const isTreasuredAsset = (this.props.item?.EditTreasureAssets || []).includes(entry.ProductId);
                    return (
                        <td className={tdclass}>
                            <CheckboxEditor
                                value={isTreasuredAsset}
                                onChange={(v) =>
                                    this.props.onTreasuredAssetChange(entry.ProductId, v, subAccount.ClAccountId)
                                }
                                cssWrapper="square-checkbox mb-0"
                                disabled={
                                    (this.props.item?.RingfencedInstruments || []).includes(entry?.InstrumentCode) ||
                                    !this.props.isEditTreasure
                                }
                            />
                        </td>
                    );
                } else {
                    return <td className="cell-center">-</td>;
                }
        }
    }

    private renderProduct(): JSX.Element {
        const item = this.props.item;
        const subTotal = this.props.item.total;
        const _class = this.props.stringHelper.getDonutClass(item.WrapperType);

        return (
            <tr className={"product-heading" + _class}>
                <td>
                    <span aria-hidden="true" className={"account-legend " + _class}></span>
                    <span className={`${_class}-text product-name`}>{item.ProductDisplayName}</span>
                    <span className="account">{item.ClAccountId}</span>
                </td>
                {this.props.cols.map((col, index) => {
                    if (col.Enabled) {
                        return this.renderRowForTotal(col.Column, subTotal, false);
                    }
                })}
            </tr>
        );
    }

    private renderProductHeader(config: { title: string; key: string }, subTotal: ITotal) {
        if (subTotal != null && subTotal.TotalCost.Amount == 0 && subTotal.MarketValue.Amount == 0) {
            return null;
        } else if (this.props.assetClassType === AssetClassType.NotSet) {
            return null;
        }
        const isheader = config.title != "Investment holdings";
        return (
            <tr className="sub-heading-main">
                <td>
                    <span className={`header ${config.key}`}>{config.title}</span>
                </td>
                {this.props.cols.map((col, index) => {
                    if (col.Enabled) {
                        return this.renderRowForTotal(col.Column, subTotal, isheader);
                    }
                })}
            </tr>
        );
    }

    private handleToggle(index: number) {
        const { toggles } = this.state;
        if (toggles.has(index)) {
            toggles.set(index, !toggles.get(index));
            this.setState({ toggles: toggles });
        }
    }

    private renderTotal(subTotal: ITotal, index: number, isModel: boolean) {
        const { keyName, cols } = this.props;
        const key = isModel ? `subTotal_${index}` : `${keyName}InvestmentGroups_${index}_SubTotal`;
        const subTotalClass = isModel ? "subtotal-nested" : "sub-total";
        return (
            <tr key={key} className={subTotalClass}>
                <td>Sub total</td>
                {cols.map((col, index) => {
                    if (col.Enabled) {
                        return this.renderRowForTotal(col.Column, subTotal, false);
                    }
                })}
            </tr>
        );
    }

    private renderManagedPortfolioRows(): JSX.Element[] {
        const { keyName, item, cols } = this.props;
        const { toggles } = this.state;
        const managedPortfolio = item.ModelPortfolioInvestments;
        if (managedPortfolio.data.length < 1) {return null;}
        const investments = managedPortfolio.data[0].Investments;
        let togglesUpdated = false;

        const trs: JSX.Element[] = [];

        investments.map((entry, i) => {
            if (!toggles.has(i)) {
                toggles.set(i, false);
                togglesUpdated = true;
            }

            const _btnClass = toggles.get(i) ? "row-toggle expanded" : "row-toggle";

            trs.push(
                <tr key={keyName + "ManagedPortfolioRows" + i}>
                    <td className="cell-hover" onClick={() => this.handleToggle(i)}>
                        <div className="row narrow-gutter">
                            <div className="col-auto col-middle">
                                <button className={_btnClass}>
                                    <span className="sr-only">Expand row</span>
                                </button>
                            </div>
                            <div className="col-fluid cell-primary col-middle">{entry.DisplayName}</div>
                        </div>
                    </td>
                    {cols.map((col, index) => {
                        if (col.Enabled) {
                            return this.renderRowForManagedPortfolio(col.Column, entry, false);
                        }
                    })}
                </tr>
            );

            if(toggles.get(i)){
                trs.push(
                    <> 
                        {this.renderRealExpendElement(entry.sub)}
                    </>
                );
            }
        });

        if (togglesUpdated) {
            this.setState({ toggles: toggles });
        }
        return trs;
    }

    private renderInvestments(
        investments: IInvestmentWithToggle[],
        hasLink: (entry) => boolean,
        index: number,
        isModel: boolean
    ): JSX.Element[] {
        if (!investments) {return null;}
        return investments.map((entry, i) => {
            const isCash = this.isCash(entry.InstrumentCode);
            const investmentTdClass = !isModel ? "" : isCash ? "" : "";
            const othersTdClass = !isModel ? "cell-right" : isCash ? "cell-right cell-10" : "cell-right";
            const key = isModel ? "rows_" + index + "_" + i : "InvestmentGroups_" + index + "_Contents_" + i;
            const hideTargetAllocation = this.props.isAssetView || isModel;
            const firstTdContent = hasLink(entry) ? (
                entry.DisplayName
            ) : (
                <button
                    className="hyperlink text-left"
                    onClick={() => {
                        this.addEvent({
                            event: "investmentstab_interaction",
                            eventParameters: {
                                eventCategory: eventCategoryForGTM.investmentsTab,
                                eventAction: "interactions",
                                eventLabel: "investment name",
                            },
                        });
                        this.setState(
                            {
                                isShowDetailModal: true,
                                instrumentCode: entry.InstrumentCode,
                                productId: entry.ProductId,
                                instrumentName: entry.DisplayName,
                                unitCost: entry.UnitCost,
                                unitPrice: entry.UnitPrice,
                                securityType: entry.Type,
                                marketValue: entry.MarketValue
                            });
                    }}
                >
                    {entry.DisplayName}
                </button>
            );
            return (
                <tr key={key} className={isModel? "sub-investment" : ""}>
                    <td className={investmentTdClass}>{firstTdContent}</td>
                    {this.props.cols.map((col, i) => {
                        if (col.Enabled) {
                            return this.renderRowForInvestment(
                                col.Column,
                                entry,
                                othersTdClass,
                                hideTargetAllocation,
                                index,
                                isModel
                            );
                        }
                    })}
                </tr>
            );
        });
    }

    private renderInvesmentsWithHeaderAndTooter(
        data: IInvestmentGroup[],
        hasLink: (entry) => boolean,
        isModel: boolean
    ): JSX.Element[] {
        if (!data) {return null;}
        let trs: JSX.Element[] = [];
        const assetClassType = this.props.assetClassType;
        if (this.props.assetClassType === AssetClassType.NotSet) {
            let combinedData: IInvestmentWithToggle[] = [];
            data.forEach((investment) => {
                combinedData = combinedData.concat(investment.Investments);
            });
            if (combinedData.length > 0) {
                combinedData.sort((a, b) => a.DisplayName.localeCompare(b.DisplayName));
                combinedData.unshift(
                    combinedData.splice(
                        combinedData.findIndex((cash) => cash.InstrumentCode == "GBPCash"),
                        1
                    )[0]
                );
            }
            return this.renderInvestments(combinedData, hasLink, 0, isModel);
        } else {
            data.forEach((item, index) => {
                //Header
                trs.push(
                    <tr className="sub-heading" key={"subHeading_" + index}>
                        <td colSpan={12}>
                            <span className="header">{item.AssetClass}</span>
                        </td>
                    </tr>
                );
                const contents: JSX.Element[] = this.renderInvestments(item.Investments, hasLink, index, isModel);
                trs = trs.concat(contents);

                //Footer
                const subTotal = item.total;
                trs.push(this.renderTotal(subTotal, index, isModel));
            });
            return trs;
        }
    }

    private renderInvestmentGroup(): JSX.Element[] {
        const data = this.props.item.IndividualInvestments?.data;
        const hasLink = (entry) => {
            return this.isCash(entry.InstrumentCode) || this.isOpi(entry.InstrumentCode);
        };
        return this.renderInvesmentsWithHeaderAndTooter(data, hasLink, false);
    }

    private renderOpiInvestmentGroup(): JSX.Element[] {
        const data = this.props.item.OffPlatformInvestments?.data;
        const hasLink = (entry) => {
            return this.isCash(entry.InstrumentCode);
        };
        return this.renderInvesmentsWithHeaderAndTooter(data, hasLink, false);
    }

    private renderEpendTrs(investmentGroups: IInvestmentGroup[]): JSX.Element[] {
        const hasLink = (entry) => {
            return this.isCash(entry.InstrumentCode);
        };
        return this.renderInvesmentsWithHeaderAndTooter(investmentGroups, hasLink, true);
    }

    private renderRealExpendElement(investmentGroups: IInvestmentGroup[]): JSX.Element {
        return (
            <>
                <tr style={{display:'none'}}>
                    {this.props.cols.map((col, index) => {
                        const tableHeaderCSS =
                            col.Column == InvestmentsColumns.Investments ? "first-cell" : "cell-right cell-10";

                        if (col.Enabled) {
                            return (
                                <th className={tableHeaderCSS} key={`asset-sub-header-${index}`}>
                                    <div>{InvestmentsColConfigHelper.getTitle(col.Column, () => {})}</div>
                                </th>
                            );
                        }
                    })}
                </tr>
                {this.renderEpendTrs(investmentGroups)}
            </>
        );
    }

    private renderInvestmentDetailPopupModal(): JSX.Element {
        const {
            isShowDetailModal,
            instrumentCode,
            instrumentName,
            unitCost,
            unitPrice,
            securityType,
            productId,
        } = this.state;

        if (!isShowDetailModal)
        {
            return null;
        }

        if (securityType === ProductType.CashProduct)
        {
            const cashProduct: DepositProduct = this.props.cashProducts[productId];
            return (
                <>
                    <DepositDetailsPopupModal
                        showBox={isShowDetailModal}
                        callBack={() => this.setState({ isShowDetailModal: false })}
                        callerSource={"Holdings"}
                        instrumentCode={instrumentCode}
                        productId={productId}
                        issuer={cashProduct?.Issuer}
                        depositType={cashProduct?.DepositProductType}
                        term={cashProduct?.Term}
                        grossRate={cashProduct?.GrossInterestRate}
                        aerRate={cashProduct?.AnnualEquivalentInterestRate}
                        minimumInvestment={cashProduct?.MinimumInvestment}
                        maximumInvestment={cashProduct?.MaximumInvestment}
                        instrumentName={instrumentName}
                        maturityDate={cashProduct?.MaturityDate}
                        holdingValue={this.getMoneyFormat(this.state.marketValue)}
                    />
                </>
            );
        }

        return (
            <>
                <InvestmentDetailsPopupModal
                    showBox={isShowDetailModal}
                    callBack={() => {
                        this.setState({ isShowDetailModal: false });
                    }}
                    instrumentCode={instrumentCode}
                    productId={productId}
                    instrumentName={instrumentName}
                    asAt={this.props.asAtDate}
                    unitCost={unitCost}
                    securityType={securityType}
                    unitPrice={unitPrice}
                ></InvestmentDetailsPopupModal>
            </>
        );
    }

    public render(): JSX.Element {
        const { keyName, isAssetView, item, assetClassType } = this.props;
        return (
            <tbody key={keyName}>
                {isAssetView ? null : this.renderProduct()}
                {this.renderProductHeader(InvestmentType.IndividualInvestments, item.IndividualInvestments.total)}
                {this.renderInvestmentGroup()}
                {this.renderProductHeader(
                    InvestmentType.ModelPortfolioInvestments,
                    item.ModelPortfolioInvestments.total
                )}
                {this.renderManagedPortfolioRows()}
                {this.renderProductHeader(InvestmentType.OffPlatformInvestments, item.OffPlatformInvestments.total)}
                {this.renderOpiInvestmentGroup()}
                {this.renderInvestmentDetailPopupModal()}
            </tbody>
        );
    }
}
